import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { apiService } from '../services/api';
import './Auth.css';

const Register = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
  });
  const [error, setError] = useState('');
  const [hint, setHint] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailStepLoading, setEmailStepLoading] = useState(false);
  const [code, setCode] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailLocked, setEmailLocked] = useState(false);
  const [emailVerificationToken, setEmailVerificationToken] = useState('');
  const [codeRequested, setCodeRequested] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [showPolicyPopover, setShowPolicyPopover] = useState(false);

  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const tokenFromLink = useMemo(() => searchParams.get('token') || '', [searchParams]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const requestCode = async () => {
    setError('');
    setHint('');
    setSuccess('');
    if (!consentChecked) {
      setError('Для продолжения необходимо согласие на обработку персональных данных');
      return;
    }
    const email = (formData.email || '').trim().toLowerCase();
    if (!email) {
      setError('Укажите email');
      return;
    }
    setEmailStepLoading(true);
    try {
      await apiService.requestEmailVerification(email);
      setCodeRequested(true);
      setEmailLocked(true);
      setHint('Мы отправили код на указанный email. Введите 6-значный код ниже или откройте ссылку из письма. Если письмо не получили, обязательно проверьте папку спама.');
    } catch (e) {
      const data = e?.response?.data;
      const msg = data?.email?.[0] || data?.error || data?.detail || 'Не удалось отправить письмо';
      setError(String(msg));
    } finally {
      setEmailStepLoading(false);
    }
  };

  const confirmCode = async () => {
    setError('');
    setHint('');
    setSuccess('');
    const email = (formData.email || '').trim().toLowerCase();
    const normalizedCode = String(code || '').trim();
    if (!email) {
      setError('Укажите email');
      return;
    }
    if (!/^[0-9]{6}$/.test(normalizedCode)) {
      setError('Введите 6-значный код');
      return;
    }
    setEmailStepLoading(true);
    try {
      const resp = await apiService.confirmEmailVerification(email, normalizedCode);
      const token = resp?.data?.email_verification_token || '';
      setEmailVerificationToken(token);
      setEmailVerified(true);
      setEmailLocked(true);
      setSuccess('Email подтвержден');
      setHint('');
    } catch (e) {
      const data = e?.response?.data;
      const msg = data?.code?.[0] || data?.error || data?.detail || 'Не удалось подтвердить код';
      setError(String(msg));
    } finally {
      setEmailStepLoading(false);
    }
  };

  // Если пользователь пришёл по ссылке из письма — подтверждаем токен и блокируем email
  useEffect(() => {
    if (!tokenFromLink) return;
    let cancelled = false;

    (async () => {
      setError('');
      setHint('');
      setSuccess('');
      setEmailStepLoading(true);
      try {
        const resp = await apiService.activateEmailVerification(tokenFromLink);
        if (cancelled) return;
        const email = resp?.data?.email || '';
        const token = resp?.data?.email_verification_token || tokenFromLink;
        if (email) {
          setFormData((prev) => ({ ...prev, email }));
        }
        setEmailVerificationToken(token);
        setEmailVerified(true);
        setEmailLocked(true);
        setCodeRequested(true);
        setSuccess('Email подтвержден по ссылке');
      } catch (e) {
        if (cancelled) return;
        const data = e?.response?.data;
        const msg = data?.error || data?.detail || 'Не удалось подтвердить ссылку';
        setError(String(msg));
      } finally {
        if (!cancelled) setEmailStepLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tokenFromLink]);

  useEffect(() => {
    if (!showPolicyPopover) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowPolicyPopover(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showPolicyPopover]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setHint('');
    setSuccess('');

    if (!emailVerified || !emailVerificationToken) {
      setError('Подтвердите email перед регистрацией');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Пароли не совпадают');
      return;
    }

    if (formData.password.length < 8) {
      setError('Пароль должен содержать минимум 8 символов');
      return;
    }
    if (!consentChecked) {
      setError('Необходимо согласие на обработку персональных данных');
      return;
    }

    setLoading(true);

    try {
      const { confirmPassword, ...registerData } = formData;
      // Добавляем password_confirm для серверной валидации
      registerData.password_confirm = formData.confirmPassword;
      registerData.email_verification_token = emailVerificationToken;
      const result = await register(registerData);
      
      if (result.success) {
        navigate('/');
      } else {
        if (typeof result.error === 'object') {
          const errorMessages = [];
          for (const [field, messages] of Object.entries(result.error)) {
            errorMessages.push(`${field}: ${messages.join(', ')}`);
          }
          setError(errorMessages.join('\n'));
        } else {
          setError(result.error);
        }
      }
    } catch (error) {
      setError('Произошла ошибка. Попробуйте снова.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-form">
        <h2>Регистрация</h2>
        {error && (
          <div className="error-message">
            {error.split('\n').map((line, index) => (
              <div key={index}>{line}</div>
            ))}
          </div>
        )}
        {success && <div className="success-message">{success}</div>}
        {hint && <div className="hint-message">{hint}</div>}
        
        <form
          onSubmit={(e) => {
            if (!emailVerified) {
              e.preventDefault();
              return;
            }
            handleSubmit(e);
          }}
        >
          <div className="form-group">
            <label htmlFor="email">Email:</label>
            <div className="email-confirm-row">
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={loading || emailLocked}
              />
              <button
                type="button"
                className="email-confirm-btn"
                onClick={requestCode}
                disabled={loading || emailStepLoading || emailVerified || !String(formData.email || '').trim() || !consentChecked}
                title={emailVerified ? 'Email уже подтвержден' : 'Отправить код подтверждения'}
              >
                {emailVerified ? 'Подтверждено' : (emailStepLoading ? 'Отправка...' : 'Подтвердить')}
              </button>
            </div>
          </div>

          {!codeRequested && (
            <div className="form-group consent-group">
              <div className="consent-box">
                <label className="consent-row">
                  <input
                    type="checkbox"
                    checked={consentChecked}
                    onChange={(e) => setConsentChecked(e.target.checked)}
                    disabled={loading}
                  />
                  <span className="consent-text">Я согласен с условиями политики конфиденциальности и даю согласие на обработку персональных данных</span>
                </label>
                <button
                  type="button"
                  className="policy-link-btn"
                  onClick={() => setShowPolicyPopover(true)}
                >
                  Подробнее
                </button>
              </div>
            </div>
          )}

          {codeRequested && !emailVerified && (
            <div className="form-group">
              <label htmlFor="email_code">Код подтверждения:</label>
              <div className="email-confirm-row">
                <input
                  type="text"
                  id="email_code"
                  name="email_code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="6 цифр"
                  disabled={loading || emailStepLoading}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                />
                <button
                  type="button"
                  className="email-confirm-btn"
                  onClick={confirmCode}
                  disabled={loading || emailStepLoading || !String(code || '').trim()}
                >
                  {emailStepLoading ? 'Проверка...' : 'Проверить'}
                </button>
              </div>
            </div>
          )}

          {emailVerified && (
            <>
              <div className="form-group">
                <label htmlFor="full_name">Полное имя:</label>
                <input
                  type="text"
                  id="full_name"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Пароль:</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  minLength={8}
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Подтвердите пароль:</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  minLength={8}
                />
              </div>

              <button type="submit" disabled={loading || !consentChecked}>
                {loading ? 'Регистрация...' : 'Зарегистрироваться'}
              </button>
            </>
          )}
        </form>

        <p className="auth-link">
          Уже есть аккаунт? <Link to="/login">Войти</Link>
        </p>
      </div>
      {showPolicyPopover && (
        <div className="policy-popover-overlay" role="dialog" aria-modal="true" onClick={() => setShowPolicyPopover(false)}>
          <div className="policy-popover" onClick={(e) => e.stopPropagation()}>
            <div className="policy-popover-header">
              <h3>Политика конфиденциальности и согласие</h3>
              <button type="button" className="policy-close-btn" onClick={() => setShowPolicyPopover(false)}>×</button>
            </div>
            <div className="policy-popover-body">
              <h4>1. Общие положения</h4>
              <p>Настоящий документ определяет порядок обработки и защиты персональных данных пользователей сервиса АОТ. Оператор обрабатывает персональные данные в соответствии с Федеральным законом от 27.07.2006 №152-ФЗ «О персональных данных».</p>

              <h4>2. Какие данные обрабатываются</h4>
              <p>Оператор может обрабатывать: фамилию, имя, отчество, адрес электронной почты, номер телефона (при наличии), данные учетной записи, технические данные о действиях в сервисе, а также иные сведения, которые пользователь добровольно предоставляет при использовании сервиса.</p>

              <h4>3. Цели обработки персональных данных</h4>
              <p>Данные обрабатываются для регистрации и идентификации пользователя, предоставления доступа к функционалу сервиса, поддержки пользователей, обеспечения безопасности, исполнения требований законодательства РФ и отправки служебных уведомлений.</p>

              <h4>4. Действия с персональными данными</h4>
              <p>Оператор вправе осуществлять сбор, запись, систематизацию, накопление, хранение, уточнение (обновление, изменение), использование, передачу (в случаях, предусмотренных законом или необходимых для работы сервиса), обезличивание, блокирование и удаление персональных данных.</p>

              <h4>5. Сроки хранения и защита данных</h4>
              <p>Персональные данные хранятся не дольше, чем это необходимо для целей обработки, если иной срок не установлен законом. Оператор принимает правовые, организационные и технические меры для защиты данных от неправомерного доступа, изменения, раскрытия или уничтожения.</p>

              <h4>6. Права пользователя</h4>
              <p>Пользователь вправе запросить сведения о своих данных, потребовать их уточнения, блокирования или удаления, а также отозвать согласие на обработку персональных данных путем обращения к Оператору. Отзыв согласия не влияет на законность обработки, выполненной до отзыва.</p>

              <h4>7. Согласие пользователя</h4>
              <p>Отмечая чекбокс на странице регистрации, пользователь свободно, своей волей и в своем интересе выражает согласие на обработку своих персональных данных на условиях настоящего документа.</p>
            </div>
            <div className="policy-popover-footer">
              <button type="button" className="policy-ok-btn" onClick={() => setShowPolicyPopover(false)}>Понятно</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Register;
